import React, { useMemo } from "react";
import {
  BookOpenText,
  Presentation,
  Video,
  FolderKanban,
  HardDrive,
  Database,
} from "lucide-react";

const normalizeType = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const typeBuckets = {
  materials: new Set(["giaotrinh", "giao_trinh"]),
  lectures: new Set(["baigiang", "bai_giang"]),
  videos: new Set(["video"]),
};

export function formatDocFileSize(bytes) {
  const n = Number(bytes) || 0;
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)} GB`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)} MB`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)} KB`;
  return `${n} B`;
}

const countCards = [
  {
    key: "materials",
    label: "Giáo trình",
    icon: BookOpenText,
    className: "bg-red-50 text-red-700 border-red-100",
  },
  {
    key: "lectures",
    label: "Bài giảng",
    icon: Presentation,
    className: "bg-blue-50 text-blue-700 border-blue-100",
  },
  {
    key: "videos",
    label: "Video",
    icon: Video,
    className: "bg-violet-50 text-violet-700 border-violet-100",
  },
  {
    key: "others",
    label: "Khác",
    icon: FolderKanban,
    className: "bg-amber-50 text-amber-800 border-amber-100",
    hint: "Bài tập, kiểm tra...",
  },
];

const sizeCards = [
  {
    key: "totalBytes",
    label: "Tổng dung lượng",
    icon: HardDrive,
    className: "bg-slate-50 text-slate-800 border-slate-200",
  },
  {
    key: "materialsBytes",
    label: "Dung lượng giáo trình",
    icon: BookOpenText,
    className: "bg-rose-50 text-rose-800 border-rose-100",
  },
  {
    key: "lecturesBytes",
    label: "Dung lượng bài giảng",
    icon: Presentation,
    className: "bg-sky-50 text-sky-800 border-sky-100",
  },
  {
    key: "videosBytes",
    label: "Dung lượng video",
    icon: Video,
    className: "bg-indigo-50 text-indigo-800 border-indigo-100",
  },
];

function StatCardGrid({ cards, stats, formatValue, dense = false }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-2 md:hidden">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.key}
              className={`rounded-xl border px-2.5 py-2 ${card.className}`}
            >
              <div className="flex items-center justify-between gap-1">
                <Icon size={14} />
                <span
                  className={`font-black leading-none tabular-nums ${
                    dense ? "text-sm" : "text-lg"
                  }`}
                >
                  {formatValue(stats[card.key])}
                </span>
              </div>
              <p className="mt-1 text-[10px] font-bold uppercase tracking-wide">
                {card.label}
              </p>
              {card.hint ? (
                <p className="mt-0.5 text-[9px] font-semibold leading-tight opacity-80">
                  {card.hint}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="hidden md:grid md:grid-cols-4 gap-3">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.key}
              className={`rounded-2xl border px-4 py-3.5 shadow-sm ${card.className}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <Icon size={16} className="shrink-0" />
                  <span className="text-[11px] font-bold uppercase tracking-wider truncate">
                    {card.label}
                  </span>
                </div>
                <span
                  className={`shrink-0 font-black leading-none tabular-nums ${
                    dense ? "text-lg" : "text-2xl"
                  }`}
                >
                  {formatValue(stats[card.key])}
                </span>
              </div>
              {card.hint ? (
                <p className="mt-1 text-[10px] font-semibold opacity-80">
                  {card.hint}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
    </>
  );
}

export default function DocumentMiniDashboard({ documents = [], stats: statsProp = null }) {
  const stats = useMemo(() => {
    if (statsProp) {
      return {
        materials: Number(statsProp.materials) || 0,
        lectures: Number(statsProp.lectures) || 0,
        videos: Number(statsProp.videos) || 0,
        others: Number(statsProp.others) || 0,
        totalBytes: Number(statsProp.totalBytes) || 0,
        materialsBytes: Number(statsProp.materialsBytes) || 0,
        lecturesBytes: Number(statsProp.lecturesBytes) || 0,
        videosBytes: Number(statsProp.videosBytes) || 0,
      };
    }

    const seed = {
      materials: 0,
      lectures: 0,
      videos: 0,
      others: 0,
      totalBytes: 0,
      materialsBytes: 0,
      lecturesBytes: 0,
      videosBytes: 0,
    };

    for (const doc of documents) {
      const type = normalizeType(doc?.type);
      const size = Number(doc?.file_size) || 0;
      seed.totalBytes += size;

      if (typeBuckets.materials.has(type)) {
        seed.materials += 1;
        seed.materialsBytes += size;
      } else if (typeBuckets.lectures.has(type)) {
        seed.lectures += 1;
        seed.lecturesBytes += size;
      } else if (typeBuckets.videos.has(type)) {
        seed.videos += 1;
        seed.videosBytes += size;
      } else {
        seed.others += 1;
      }
    }
    return seed;
  }, [documents, statsProp]);

  return (
    <div className="mb-5 space-y-3">
      <StatCardGrid
        cards={countCards}
        stats={stats}
        formatValue={(v) => v}
      />
      <div className="flex items-center gap-2 px-0.5">
        <Database size={12} className="text-slate-400" />
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
          Dung lượng theo loại (bộ lọc hiện tại)
        </p>
      </div>
      <StatCardGrid
        cards={sizeCards}
        stats={stats}
        formatValue={formatDocFileSize}
        dense
      />
    </div>
  );
}
